import {
  FocusContext,
  FocusableComponentLayout,
  useFocusable,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, { useCallback, useState, useMemo } from 'react'
import cx from 'classnames'
import { platformTransitionStyle, throttle } from '../../../../shared/helpers.js'

import type { CollectionLanesProps } from './types'

const COLLECTIONS_SET_PADDING_TOP = 227

function CollectionLanes<T>({
  collections,
  singleRow = false,
  children,
  hasHeaderComponent = false,
  className = '',
  handelWheelExternally = false,
  onExternalFocus,
}: CollectionLanesProps<T>) {
  const [translateY, setTranslateY] = useState(0)
  const classes = cx('collections-set', { [className]: !!className })

  const { ref, focusKey } = useFocusable({
    onBlur: () => {
      if (hasHeaderComponent) setTranslateY(0)
    },
  })

  const handleCarouselFocus = useCallback(
    (layout: FocusableComponentLayout) => {
      if (handelWheelExternally && onExternalFocus) {
        onExternalFocus(layout)
      } else if (!singleRow) {
        setTranslateY(-(layout.top - COLLECTIONS_SET_PADDING_TOP))
      }
    },
    [setTranslateY, singleRow, handelWheelExternally, onExternalFocus]
  )

  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const deltaY = event.deltaY
        if (deltaY > 0) {
          setTranslateY((prev) =>
            Math.max(
              -ref.current.getBoundingClientRect().height + 500,
              prev - 500
            )
          )
        } else if (deltaY < 0) {
          setTranslateY((prev) => Math.min(0, prev + 500))
        }
        setFocus(focusKey)
      }, 600),
    [focusKey]
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={classes}
        style={{
          transform: handelWheelExternally
            ? undefined
            : `translateY(${translateY}px)`,
          transition: handelWheelExternally
            ? undefined
            : platformTransitionStyle('transform 500ms ease-in-out'),
        }}
        onWheel={handelWheelExternally ? undefined : handleWheel}
      >
        {collections.length > 0
          ? collections.map((collection, index) =>
              children(collection, handleCarouselFocus, index)
            )
          : null}
      </div>
    </FocusContext.Provider>
  )
}

export default CollectionLanes
